/** @file Editor WebGL2 renderer over RenderBuffers chunks: one VAO, one dynamic buffer, blend table. */
import type { Camera, RenderBuffers, TexturePages } from '@pose2d/geometry/types';
import type { RgbaImage } from '@pose2d/math/types';
import { invariant } from '@pose2d/std/invariant';
import { backingSize } from './_internal/backingSize';
import { checkPageImages } from './_internal/checkPageImages';
import { createFrameMemo } from './_internal/createFrameMemo';
import type { FrameMemo } from './_internal/createFrameMemo';
import { initGpu } from './_internal/initGpu';
import type { Gpu } from './_internal/initGpu';
import { paintFrame } from './_internal/paintFrame';
import type { FrameScratch } from './_internal/paintFrame';
import type { ViewportRenderer } from './viewportRenderer';

/** No MSAA, depth or stencil (spec §7.6); premultiplied canvas alpha; the drawing buffer is not preserved. */
const ATTRIBUTES: WebGLContextAttributes = {
  alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, preserveDrawingBuffer: false,
};

/** Mutable renderer state shared by the methods and the context-loss listeners. */
interface State {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  gpu: Gpu | null;
  pages: { textures: TexturePages; images: RgbaImage[] } | null;
  memo: FrameMemo;
  scratch: FrameScratch;
  disposed: boolean;
}

/** Context lost: prevent the default so the browser may restore it, and forget every GPU handle. */
function onLost(s: State, event: Event): void {
  event.preventDefault();
  s.gpu?.chunks.dispose(true);
  s.gpu?.pages.dispose(true);
  s.gpu = null;
}

/** Context restored: rebuild state, program, VAO, buffers and every page texture; the next draw renders. */
function onRestored(s: State): void {
  if (s.disposed) return;
  s.memo.invalidate();
  s.gpu = initGpu(s.gl);
  if (s.pages) s.gpu.pages.upload(s.pages.textures, s.pages.images);
}

/** Backing store = round(css × dpr); only a real size change (which clears the canvas) forces a redraw. */
function resize(s: State, width: number, height: number, dpr: number): void {
  if (s.disposed) return;
  const size = backingSize(width, height, dpr);
  if (s.canvas.width === size.width && s.canvas.height === size.height) return;
  s.canvas.width = size.width;
  s.canvas.height = size.height;
  s.memo.invalidate();
}

/** Validates and uploads the page table; while the context is lost it runs the GL-free checks and keeps it. */
function uploadPages(s: State, textures: TexturePages, images: RgbaImage[]): void {
  if (s.disposed) return;
  if (s.gpu === null) checkPageImages(textures, images);
  else s.gpu.pages.upload(textures, images);
  s.pages = { textures, images };
  s.memo.invalidate();
}

/** Paints unless disposed, lost or unchanged; a frame that throws is forgotten so the next one retries. */
function draw(s: State, chunks: RenderBuffers[], camera: Camera, background: number): void {
  if (s.disposed || s.gpu === null || !s.memo.changed(chunks, camera, background)) return;
  let painted = false;
  try {
    paintFrame(s.gl, s.gpu, chunks, camera, background, s.scratch);
    painted = true;
  } finally {
    if (!painted) s.memo.invalidate();
  }
}

/** Deletes every GPU object; the state stays disposed so later calls are no-ops. */
function release(s: State): void {
  s.disposed = true;
  if (s.gpu) {
    s.gpu.chunks.dispose(false);
    s.gpu.pages.dispose(false);
    s.gl.deleteProgram(s.gpu.program.program);
  }
  s.gpu = null;
  s.pages = null;
}

/** Creates the renderer on a canvas (WebGL2 required). */
export function createViewportRenderer(canvas: HTMLCanvasElement): ViewportRenderer {
  const gl = canvas.getContext('webgl2', ATTRIBUTES);
  invariant(gl, 'WebGL2 is not available on this canvas');
  const s: State = {
    gl, canvas, gpu: initGpu(gl), pages: null, memo: createFrameMemo(),
    scratch: { rows: new Float32Array(6), clear: new Float32Array(4) }, disposed: false,
  };
  const lost = (event: Event): void => { onLost(s, event); };
  const restored = (): void => { onRestored(s); };
  canvas.addEventListener('webglcontextlost', lost);
  canvas.addEventListener('webglcontextrestored', restored);
  return {
    resize: (width, height, dpr) => { resize(s, width, height, dpr); },
    uploadPages: (textures, images) => { uploadPages(s, textures, images); },
    draw: (chunks, camera, background) => { draw(s, chunks, camera, background); },
    dispose: () => {
      if (s.disposed) return;
      canvas.removeEventListener('webglcontextlost', lost);
      canvas.removeEventListener('webglcontextrestored', restored);
      release(s);
    },
  };
}
